<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <div class="sidebar-header">
        <button class="toggle-btn" @click="toggleSidebar">
          <span v-if="!isCollapsed">☰</span>
          <span v-else>→</span>
        </button>
        <h1 v-if="!isCollapsed" class="sidebar-title">Property Management</h1>
      </div>
      
      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item">
          <span class="nav-icon">🏠</span>
          <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
        </router-link>
        <router-link to="/reservations" class="nav-item">
          <span class="nav-icon">📋</span>
          <span v-if="!isCollapsed" class="nav-text">Reservations</span>
        </router-link>
        <router-link to="/properties" class="nav-item">
          <span class="nav-icon">🏨</span>
          <span v-if="!isCollapsed" class="nav-text">Properties</span>
        </router-link>
        <a @click="goToEmailContacts" class="nav-item" style="cursor: pointer;">
          <span class="nav-icon">📧</span>
          <span v-if="!isCollapsed" class="nav-text">Email Contacts</span>
        </a>
        <router-link to="/settings" class="nav-item" active-class="active">
          <span class="nav-icon">⚙️</span>
          <span v-if="!isCollapsed" class="nav-text">Settings</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-header">
        <h1>Settings</h1>
        <div class="user-info">
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <!-- Property Selection -->
      <div class="section-card">
        <div class="section-header">
          <h2>Property Configuration</h2>
          <p class="section-description">Configure calculation parameters for each property</p>
        </div>
        
        <div class="property-selector">
          <label class="selector-label">Select Property:</label>
          <select v-model="selectedPropertyId" @change="loadPropertySettings" class="property-select">
            <option value="">Choose a property...</option>
            <option v-for="property in properties" :key="property.id" :value="property.id">
              {{ property.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Property Settings Form -->
      <div v-if="selectedPropertyId && currentPropertySettings" class="section-card">
        <div class="section-header">
          <h2>{{ getSelectedPropertyName() }} - Settings</h2>
        </div>
        
        <div class="settings-grid">
          <!-- Management Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Management Fee Configuration</h3>
          </div>

          <!-- Management Fee Rate -->
          <div class="setting-group">
            <label class="setting-label">Management Fee Rate (%)</label>
            <div class="input-group">
              <input 
                type="number" 
                v-model="currentPropertySettings.managementFeeRate" 
                @input="markAsModified"
                step="0.1" 
                min="0" 
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <!-- Management Fee Rate -->
          <div class="radio-group">
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="revenue"
      @change="markAsModified"
    >
    <span>Total Revenue (Gross Sales) - before any deductions</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="net"
      @change="markAsModified"
    >
    <span>Net Income - after platform fees and card fees</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="gross"
      @change="markAsModified"
    >
    <span>Gross Profit (after all direct expenses) - percentage</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="grossFixed"
      @change="markAsModified"
    >
    <span>Gross Profit (after all direct expenses) - fixed amount</span>
  </label>
</div>
<p class="setting-description">
  Choose whether management fees are calculated on total revenue (highest), net income (medium), gross profit percentage, or a fixed monthly amount
</p>

          <!-- Fixed Management Fee Amount (conditional) -->
          <div v-if="currentPropertySettings.managementFeeBase === 'grossFixed'" class="setting-group">
            <label class="setting-label">
              Fixed Management Fee Amount (£)
              <span v-if="hasMonthSpecificFee" class="month-specific-badge" title="This value is specific to the selected month only">
                (Month-specific)
              </span>
            </label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.managementFeeFixedAmount"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Month Selector (conditional - only for grossFixed) -->
          <div v-if="currentPropertySettings.managementFeeBase === 'grossFixed'" class="setting-group">
            <label class="setting-label">Apply to Specific Month (Optional)</label>
            <input
              type="month"
              v-model="selectedMonth"
              @change="onMonthChange"
              class="setting-input"
              placeholder="YYYY-MM"
            >
            <p class="setting-description">
              Leave empty to set as default for all months, or select a specific month to override just that month
            </p>
          </div>

          <!-- VAT Rate -->
          <div class="setting-group">
            <label class="setting-label">VAT Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.vatRate"
                @input="markAsModified"
                step="0.1"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <!-- VAT Business Model -->
          <div class="setting-group full-width">
            <label class="setting-label">VAT Business Model</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.vatBusinessModel"
                  value="agent"
                  @change="markAsModified"
                >
                <div class="radio-content">
                  <strong>Agent Only</strong>
                  <p class="radio-description">VAT applies only to management fees, consumables, and laundry services supplied by your company. Cheaper for clients.</p>
                </div>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.vatBusinessModel"
                  value="principal"
                  @change="markAsModified"
                >
                <div class="radio-content">
                  <strong>Principal (Full Control)</strong>
                  <p class="radio-description">VAT applies to all services including maintenance and cleaning. Your company takes full responsibility.</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Laundry & Linen Supply - Fixed Monthly -->
          <div class="setting-group">
            <label class="setting-label">Laundry & Linen Supply - Fixed Monthly (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.laundryFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Fixed monthly laundry cost (used when "Fixed" mode is selected in preview)</p>
          </div>

          <!-- Laundry Cost per Booking -->
          <div class="setting-group">
            <label class="setting-label">Laundry Cost per Booking (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.laundryPerBooking"
                @input="markAsModified"
                step="0.01"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Cost per booking (used when "Per Booking" mode is selected in preview)</p>
          </div>

          <!-- Laundry VAT Applicable -->
          <div class="setting-group">
            <label class="setting-label">Apply VAT on Laundry</label>
            <div class="toggle-container">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="currentPropertySettings.laundryVatApplicable"
                  @change="markAsModified"
                >
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">{{ currentPropertySettings.laundryVatApplicable ? 'VAT Applied' : 'No VAT' }}</span>
            </div>
            <p class="setting-description">Enable if laundry service is supplied by your company (Agent model)</p>
          </div>

          <!-- Monthly Costs Section -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Monthly Variable Costs</h3>
          </div>

          <!-- Maintenance -->
          <div class="setting-group">
            <label class="setting-label">Maintenance (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.maintenance" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Consumables -->
          <div class="setting-group">
            <label class="setting-label">Consumables (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.consumables" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Other Expenses -->
          <div class="setting-group">
            <label class="setting-label">Other Expenses (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.otherExpenses" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Cleaning Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Cleaning Fee Configuration</h3>
          </div>

          <!-- Cleaning Fee - Fixed Monthly -->
          <div class="setting-group">
            <label class="setting-label">Cleaning Fee - Fixed Monthly (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.cleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Fixed monthly cleaning cost (used when "Fixed Monthly Amount" mode is selected in preview)</p>
          </div>

          <!-- Cleaning Cost per Booking -->
          <div class="setting-group">
            <label class="setting-label">Cleaning Cost per Booking (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.cleaningPerBooking"
                @input="markAsModified"
                step="0.01"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Cost per booking (used when "Per Booking" mode is selected in preview)</p>
          </div>

          <!-- Custom Rates per Platform (for 'monthly' mode in preview) -->
          <div class="setting-group">
            <label class="setting-label">Booking.com Cleaning Fee (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.bookingCleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
              <span class="input-suffix">per booking</span>
            </div>
            <p class="setting-description">Used when "Custom Rates per Platform" mode is selected in preview</p>
          </div>

          <div class="setting-group">
            <label class="setting-label">Other Platforms Cleaning Fee (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.otherCleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
              <span class="input-suffix">per booking</span>
            </div>
            <p class="setting-description">
              Applied to Airbnb, VRBO, Direct bookings (used when "Custom Rates per Platform" mode is selected in preview)
            </p>
          </div>

          <!-- Card Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Card Fee Configuration</h3>
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Card Fee Mode</label>
            <div class="radio-group">
              <label class="radio-option">
                <input 
                  type="radio" 
                  v-model="currentPropertySettings.cardFeeMode" 
                  value="rentlio"
                  @change="markAsModified"
                >
                <span>Use Rentlio Data (Booking.com only)</span>
              </label>
              <label class="radio-option">
                <input 
                  type="radio" 
                  v-model="currentPropertySettings.cardFeeMode" 
                  value="custom"
                  @change="markAsModified"
                >
                <span>Custom Calculation</span>
              </label>
            </div>
          </div>

          <div v-if="currentPropertySettings.cardFeeMode === 'custom'" class="setting-group">
            <label class="setting-label">Card Fee Rate (%)</label>
            <div class="input-group">
              <input 
                type="number" 
                v-model="currentPropertySettings.customCardFeeRate" 
                @input="markAsModified"
                step="0.01" 
                min="0" 
                max="10"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <div v-if="currentPropertySettings.cardFeeMode === 'custom'" class="setting-group">
            <label class="setting-label">Card Fee Fixed (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.customCardFeeFixed" 
                @input="markAsModified"
                step="0.01" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Platform Commission Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Platform Commission Configuration</h3>
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Commission Mode</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.platformFeeMode"
                  value="rentlio"
                  @change="markAsModified"
                >
                <span>Use Rentlio Data</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.platformFeeMode"
                  value="custom"
                  @change="markAsModified"
                >
                <span>Custom Calculation by Platform</span>
              </label>
            </div>
          </div>

          <!-- Booking.com Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomBookingCom"
                @change="markAsModified"
              >
              <span>Apply custom commission for Booking.com</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomBookingCom" class="setting-group">
            <label class="setting-label">Booking.com Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.bookingComCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>

          <!-- Airbnb Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomAirbnb"
                @change="markAsModified"
              >
              <span>Apply custom commission for Airbnb</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomAirbnb" class="setting-group">
            <label class="setting-label">Airbnb Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.airbnbCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>

          <!-- Other Platforms Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomOther"
                @change="markAsModified"
              >
              <span>Apply custom commission for Other Platforms</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomOther" class="setting-group">
            <label class="setting-label">Other Platforms Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.otherPlatformCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>
        </div>

        <!-- Save Actions -->
        <div class="actions-section">
          <div class="actions-card">
            <div class="actions-content">
              <div class="actions-info">
                <h3>Save Configuration</h3>
                <p v-if="hasUnsavedChanges" class="warning-text">You have unsaved changes for {{ getSelectedPropertyName() }}</p>
                <p v-else class="success-text">All changes saved</p>
              </div>
              <div class="actions-buttons">
                <button @click="resetPropertySettings" class="btn btn-outline" :disabled="!hasUnsavedChanges">
                  Reset to Default
                </button>
                <button @click="savePropertySettings" class="btn btn-primary" :disabled="!hasUnsavedChanges || saving">
                  <span v-if="saving">Saving...</span>
                  <span v-else>Save Changes</span>
                </button>
                <button
                  v-if="currentPropertySettings && currentPropertySettings.managementFeeBase === 'grossFixed' && hasFixedAmountChanged && selectedMonth"
                  @click="saveForThisMonthOnly"
                  :disabled="!hasFixedAmountChanged || saving"
                  class="btn btn-secondary"
                  title="Save this fixed fee amount for the selected month only, without affecting other months"
                >
                  <span v-if="saving">Saving...</span>
                  <span v-else>Save for Selected Month Only</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div v-if="!selectedPropertyId" class="section-card">
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <h3>Select a Property</h3>
          <p>Choose a property above to configure its calculation parameters</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';

export default {
  name: 'SettingsView',
  data() {
    return {
      isCollapsed: false,
      hasUnsavedChanges: false,
      saving: false,
      successMessage: '',
      errorMessage: '',

      // Properties data
      properties: [],
      selectedPropertyId: '',
      currentPropertySettings: null,
      originalSettings: {}, // Pour comparer les changements
      hasMonthSpecificFee: false, // Indique si un montant spécifique au mois est chargé
      selectedMonth: '', // Format: 'YYYY-MM' pour le sélecteur de mois

      // Default settings template - UPDATED WITH NEW VAT FIELDS
      defaultSettings: {
        managementFeeRate: 16,
        managementFeeBase: 'net', // 'revenue', 'net', 'gross', 'grossFixed'
        managementFeeFixedAmount: 500, // Fixed amount for grossFixed mode
        vatRate: 20,
        vatBusinessModel: 'agent', // 'agent' or 'principal'
        laundryFee: 0, // Monthly laundry & linen supply cost
        laundryCalculationMode: 'fixed', // 'fixed' or 'per_booking'
        laundryPerBooking: 15.00, // Cost per booking for per_booking mode
        laundryVatApplicable: true, // Apply VAT on laundry services
        maintenance: 0,
        consumables: 20,
        otherExpenses: 0,
        cleaningMode: 'rentlio',
        cleaningFee: 0, // Fixed monthly cleaning cost
        cleaningPerBooking: 150.00, // Cost per booking for per_booking mode
        bookingCleaningFee: 150, // For 'monthly' mode (Custom Rates) - Booking.com
        otherCleaningFee: 120, // For 'monthly' mode (Custom Rates) - Other platforms
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30,
        platformFeeMode: 'rentlio',
        applyCustomBookingCom: false,
        bookingComCommissionRate: 15.00,
        applyCustomAirbnb: false,
        airbnbCommissionRate: 3.00,
        applyCustomOther: false,
        otherPlatformCommissionRate: 10.00
      },

      baseUrl: SETTINGS.API.baseUrl
    };
  },

  computed: {
    hasFixedAmountChanged() {
      // Vérifie si seul le fixed amount a changé (pas les autres paramètres)
      if (!this.hasUnsavedChanges || !this.currentPropertySettings || this.currentPropertySettings.managementFeeBase !== 'grossFixed') {
        return false;
      }
      // Compare uniquement le fixed amount avec l'original
      return parseFloat(this.currentPropertySettings.managementFeeFixedAmount) !== parseFloat(this.originalSettings.managementFeeFixedAmount);
    }
  },
  
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    markAsModified() {
      this.hasUnsavedChanges = true;
      this.clearMessages();
    },
    
    clearMessages() {
      this.successMessage = '';
      this.errorMessage = '';
    },
    
    getSelectedPropertyName() {
      const property = this.properties.find(p => p.id === this.selectedPropertyId);
      return property ? property.name : '';
    },
    
    async fetchProperties() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      try {
        // 1. Récupérer la config PMS de l'utilisateur
        const configResponse = await axios.get(`${this.baseUrl}/api/users/pms-configs/`, { headers });

        // L'API retourne une réponse paginée: { count, results: [...] }
        const pmsConfigs = configResponse.data.results || configResponse.data;

        if (!pmsConfigs || !Array.isArray(pmsConfigs) || pmsConfigs.length === 0) {
          console.error('No PMS configuration found');
          this.errorMessage = 'No PMS configuration found. Please configure your PMS connection first.';
          this.properties = [];
          return;
        }

        const pmsConfigId = pmsConfigs[0].id;

        // 2. Récupérer les propriétés avec le pms_config_id
        const response = await axios.get(
          `${this.baseUrl}/api/pms/properties?pms_config_id=${pmsConfigId}`,
          { headers }
        );

        // 3. Gérer la structure de réponse
        if (response.data.status === 'success') {
          this.properties = response.data.properties.data || [];
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.errorMessage = 'Failed to load properties. Please try again.';
        }
      }
    },
    
    async loadPropertySettings() {
      if (!this.selectedPropertyId) {
        this.currentPropertySettings = null;
        this.hasUnsavedChanges = false;
        this.selectedMonth = '';
        this.hasMonthSpecificFee = false;
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      try {
        const response = await axios.get(
          `${this.baseUrl}/api/pms/settings/${this.selectedPropertyId}/`,
          { headers }
        );

        if (response.data.success) {
          // Map backend field names to frontend (snake_case to camelCase)
          const backendData = response.data.data;
          this.currentPropertySettings = {
            managementFeeRate: parseFloat(backendData.management_fee_rate || backendData.managementFeeRate || 16),
            managementFeeBase: backendData.management_fee_base || backendData.managementFeeBase || 'net',
            managementFeeFixedAmount: parseFloat(backendData.management_fee_fixed_amount || backendData.managementFeeFixedAmount || 500),
            vatRate: parseFloat(backendData.vat_rate || backendData.vatRate || 20),
            vatBusinessModel: backendData.vat_business_model || backendData.vatBusinessModel || 'agent',
            laundryFee: parseFloat(backendData.laundry_fee || backendData.laundryFee || 0),
            laundryCalculationMode: backendData.laundry_calculation_mode || backendData.laundryCalculationMode || 'fixed',
            laundryPerBooking: parseFloat(backendData.laundry_per_booking || backendData.laundryPerBooking || 15.00),
            laundryVatApplicable: backendData.laundry_vat_applicable !== undefined ? backendData.laundry_vat_applicable : (backendData.laundryVatApplicable !== undefined ? backendData.laundryVatApplicable : true),
            maintenance: parseFloat(backendData.maintenance || 0),
            consumables: parseFloat(backendData.consumables || 20),
            otherExpenses: parseFloat(backendData.other_expenses || backendData.otherExpenses || 0),
            cleaningMode: backendData.cleaning_mode || backendData.cleaningMode || 'rentlio',
            cleaningFee: parseFloat(backendData.cleaning_fee || backendData.cleaningFee || 0),
            cleaningPerBooking: parseFloat(backendData.cleaning_per_booking || backendData.cleaningPerBooking || 150.00),
            bookingCleaningFee: parseFloat(backendData.booking_cleaning_fee || backendData.bookingCleaningFee || 150),
            otherCleaningFee: parseFloat(backendData.other_cleaning_fee || backendData.otherCleaningFee || 120),
            cardFeeMode: backendData.card_fee_mode || backendData.cardFeeMode || 'rentlio',
            customCardFeeRate: parseFloat(backendData.custom_card_fee_rate || backendData.customCardFeeRate || 2.9),
            customCardFeeFixed: parseFloat(backendData.custom_card_fee_fixed || backendData.customCardFeeFixed || 0.30),
            platformFeeMode: backendData.platform_fee_mode || backendData.platformFeeMode || 'rentlio',
            applyCustomBookingCom: backendData.apply_custom_booking_com || backendData.applyCustomBookingCom || false,
            bookingComCommissionRate: parseFloat(backendData.booking_com_commission_rate || backendData.bookingComCommissionRate || 15.00),
            applyCustomAirbnb: backendData.apply_custom_airbnb || backendData.applyCustomAirbnb || false,
            airbnbCommissionRate: parseFloat(backendData.airbnb_commission_rate || backendData.airbnbCommissionRate || 3.00),
            applyCustomOther: backendData.apply_custom_other || backendData.applyCustomOther || false,
            otherPlatformCommissionRate: parseFloat(backendData.other_platform_commission_rate || backendData.otherPlatformCommissionRate || 10.00)
          };
        }
      } catch (error) {
        console.error('Error loading property settings:', error);
        // Use defaults on error
        this.currentPropertySettings = { ...this.defaultSettings };
      }

      // Store original settings for comparison
      this.originalSettings = { ...this.currentPropertySettings };
      this.hasUnsavedChanges = false;
      this.selectedMonth = '';
      this.hasMonthSpecificFee = false;
      this.clearMessages();
    },
    
    async savePropertySettings() {
      if (!this.selectedPropertyId || !this.currentPropertySettings) return;

      this.saving = true;
      this.clearMessages();

      try {
        // Validate settings
        if (!this.validateSettings()) {
          this.errorMessage = 'Please check your settings values.';
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const headers = { 'Authorization': `Bearer ${token}` };

        // Map frontend field names to backend (camelCase to snake_case)
        const payload = {
          property_id: this.selectedPropertyId,
          management_fee_rate: this.currentPropertySettings.managementFeeRate,
          management_fee_base: this.currentPropertySettings.managementFeeBase,
          management_fee_fixed_amount: this.currentPropertySettings.managementFeeFixedAmount,
          vat_rate: this.currentPropertySettings.vatRate,
          vat_business_model: this.currentPropertySettings.vatBusinessModel,
          laundry_fee: this.currentPropertySettings.laundryFee,
          laundry_calculation_mode: this.currentPropertySettings.laundryCalculationMode,
          laundry_per_booking: this.currentPropertySettings.laundryPerBooking,
          laundry_vat_applicable: this.currentPropertySettings.laundryVatApplicable,
          maintenance: this.currentPropertySettings.maintenance,
          consumables: this.currentPropertySettings.consumables,
          other_expenses: this.currentPropertySettings.otherExpenses,
          cleaning_mode: this.currentPropertySettings.cleaningMode,
          cleaning_fee: this.currentPropertySettings.cleaningFee,
          cleaning_per_booking: this.currentPropertySettings.cleaningPerBooking,
          booking_cleaning_fee: this.currentPropertySettings.bookingCleaningFee,
          other_cleaning_fee: this.currentPropertySettings.otherCleaningFee,
          card_fee_mode: this.currentPropertySettings.cardFeeMode,
          custom_card_fee_rate: this.currentPropertySettings.customCardFeeRate,
          custom_card_fee_fixed: this.currentPropertySettings.customCardFeeFixed,
          platform_fee_mode: this.currentPropertySettings.platformFeeMode,
          apply_custom_booking_com: this.currentPropertySettings.applyCustomBookingCom,
          booking_com_commission_rate: this.currentPropertySettings.bookingComCommissionRate,
          apply_custom_airbnb: this.currentPropertySettings.applyCustomAirbnb,
          airbnb_commission_rate: this.currentPropertySettings.airbnbCommissionRate,
          apply_custom_other: this.currentPropertySettings.applyCustomOther,
          other_platform_commission_rate: this.currentPropertySettings.otherPlatformCommissionRate
        };

        const response = await axios.post(
          `${this.baseUrl}/api/pms/settings/`,
          payload,
          { headers }
        );

        if (response.data.success) {
          this.hasUnsavedChanges = false;
          this.successMessage = `Settings saved for ${this.getSelectedPropertyName()}!`;

          // Auto-clear success message
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        } else {
          this.errorMessage = response.data.message || 'Failed to save settings.';
        }

      } catch (error) {
        console.error('Error saving settings:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.errorMessage = error.response?.data?.message || 'Failed to save settings. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },
    
    validateSettings() {
      if (!this.currentPropertySettings) return false;

      const s = this.currentPropertySettings;

      // Check percentage rates
      if (s.managementFeeRate < 0 || s.managementFeeRate > 100) return false;
      if (s.vatRate < 0 || s.vatRate > 100) return false;
      if (s.customCardFeeRate < 0 || s.customCardFeeRate > 10) return false;

      // Check monetary values
      if (s.maintenance < 0 || s.consumables < 0 || s.otherExpenses < 0) return false;
      if (s.monthlyCleaningFee < 0 || s.customCardFeeFixed < 0) return false;
      if (s.managementFeeFixedAmount < 0) return false;
      if (s.laundryFee < 0) return false;
      if (s.cleaningFee < 0 || s.cleaningPerBooking < 0) return false;

      // Check management fee base
      if (!['gross', 'net', 'revenue', 'grossFixed'].includes(s.managementFeeBase)) return false;

      return true;
    },
    
    resetPropertySettings() {
      if (!this.selectedPropertyId) return;
      
      if (confirm(`Reset all settings for ${this.getSelectedPropertyName()} to default values?`)) {
        this.currentPropertySettings = { ...this.defaultSettings };
        this.hasUnsavedChanges = true;
        this.clearMessages();
      }
    },

    async onMonthChange() {
      // Quand l'utilisateur sélectionne un mois, vérifier s'il existe un montant spécifique
      if (this.selectedMonth && this.selectedPropertyId) {
        const [year, month] = this.selectedMonth.split('-');
        await this.checkMonthSpecificFee(this.selectedPropertyId, parseInt(year), parseInt(month));
      } else {
        // Revenir aux valeurs par défaut
        this.hasMonthSpecificFee = false;
        await this.loadPropertySettings();
      }
    },

    async checkMonthSpecificFee(propertyId, year, month) {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = { 'Authorization': `Bearer ${token}` };

        // Appeler l'API pour vérifier s'il existe un montant spécifique
        const response = await axios.get(
          `${this.baseUrl}/api/pms/report-fixed-fee/`,
          {
            params: { property_id: propertyId, year: year, month: month },
            headers
          }
        );

        if (response.data.success && response.data.data && response.data.data.fixed_amount !== null) {
          // Un montant spécifique existe pour ce mois
          this.currentPropertySettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
          this.originalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
          this.hasMonthSpecificFee = true;
          this.hasUnsavedChanges = false;
          console.log(`Month-specific fee loaded: £${response.data.data.fixed_amount} for ${year}-${month}`);
        } else {
          // Pas de montant spécifique, utiliser le défaut
          this.hasMonthSpecificFee = false;
        }

      } catch (error) {
        console.error('Error checking month-specific fee:', error);
        this.hasMonthSpecificFee = false;
      }
    },

    async saveForThisMonthOnly() {
      if (!this.selectedPropertyId || !this.currentPropertySettings || !this.selectedMonth) return;

      this.saving = true;
      this.clearMessages();

      try {
        const [year, month] = this.selectedMonth.split('-');

        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const headers = { 'Authorization': `Bearer ${token}` };

        // Payload pour sauvegarder le fixed fee pour ce mois uniquement
        const payload = {
          property_id: this.selectedPropertyId,
          year: parseInt(year),
          month: parseInt(month),
          fixed_amount: this.currentPropertySettings.managementFeeFixedAmount
        };

        const response = await axios.post(
          `${this.baseUrl}/api/pms/report-fixed-fee/`,
          payload,
          { headers }
        );

        if (response.data.success) {
          this.hasMonthSpecificFee = true;
          this.originalSettings.managementFeeFixedAmount = this.currentPropertySettings.managementFeeFixedAmount;
          this.hasUnsavedChanges = false;

          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December'];
          const monthName = monthNames[parseInt(month) - 1];

          this.successMessage = `Fixed Management Fee of £${this.currentPropertySettings.managementFeeFixedAmount} saved for ${monthName} ${year} only! Other months will continue to use the default setting.`;

          // Auto-clear success message
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        } else {
          this.errorMessage = response.data.message || 'Failed to save month-specific fee.';
        }

      } catch (error) {
        console.error('Error saving month-specific fee:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.errorMessage = error.response?.data?.message || 'Failed to save month-specific fee. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    goToEmailContacts() {
      this.$router.push('/reservations?openContacts=true');
    }
  },
  
  mounted() {
    this.fetchProperties();
  }
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
}

.sidebar-collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.toggle-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-bottom: 15px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.sidebar-nav {
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0 10px;
  border-radius: 10px;
}

.nav-item:hover,
.nav-item.active {
  background: rgba(255,255,255,0.1);
  color: white;
  transform: translateX(5px);
}

.nav-icon {
  font-size: 18px;
  margin-right: 15px;
  width: 20px;
}

.nav-text {
  font-weight: 500;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 30px;
  transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
  margin-left: 70px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.content-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* Alerts */
.alert {
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
}

.alert-success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.alert-error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  margin-bottom: 30px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.section-header {
  padding: 25px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
  margin: 0 0 5px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.section-description {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

/* Property Configuration Styles */
.property-selector {
  padding: 25px 30px;
}

.selector-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.property-select {
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.property-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  padding: 30px;
}

.setting-group {
  display: flex;
  flex-direction: column;
}

.setting-group.full-width {
  grid-column: 1 / -1;
}

.setting-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 20px 0 10px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.input-prefix,
.input-suffix {
  position: absolute;
  color: #6b7280;
  font-weight: 500;
  z-index: 2;
  pointer-events: none;
}

.input-prefix {
  left: 12px;
}

.input-suffix {
  right: 12px;
}

.setting-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.setting-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-prefix + .setting-input {
  padding-left: 45px;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.radio-option:hover {
  border-color: #667eea;
  background: #f8fafc;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.radio-option input[type="radio"]:checked + .radio-content strong {
  color: #667eea;
}

.radio-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.radio-content strong {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  transition: color 0.3s ease;
}

.radio-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle-switch input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #667eea;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
}

.toggle-label {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
}

.setting-description {
  font-size: 12px;
  color: #6b7280;
  margin: 8px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}

/* Actions Section */
.actions-section {
  margin-top: 0;
  border-top: 1px solid #e2e8f0;
}

.actions-card {
  background: white;
  border-radius: 0;
}

.actions-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
}

.actions-info h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.warning-text {
  margin: 0;
  font-size: 14px;
  color: #d97706;
  font-weight: 500;
}

.success-text {
  margin: 0;
  font-size: 14px;
  color: #059669;
  font-weight: 500;
}

.actions-buttons {
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.btn-secondary:hover:not(:disabled) {
  background: #d97706;
  transform: translateY(-1px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}

.btn-outline:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

/* Month-specific badge */
.month-specific-badge {
  display: inline-block;
  background: #fbbf24;
  color: #78350f;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  vertical-align: middle;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #1a202c;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
  max-width: 400px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  
  .main-content {
    margin-left: 0;
    padding: 20px 15px;
  }
  
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 20px;
  }
  
  .actions-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
  
  .actions-buttons {
    width: 100%;
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
  
  .radio-group {
    gap: 8px;
  }
  
  .radio-option {
    padding: 10px 12px;
  }

  .cleaning-fees-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
}
</style>